import { marked } from "marked";
import React, { useEffect, useRef, useState } from "react";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { apiFetch } from "../apiKeyBus";

// API-based URL definitions
const API_BASE_URL = "";
console.log("API_BASE_URL =", API_BASE_URL);

// ustomize the marked link renderer
const renderer = new marked.Renderer();
renderer.link = function (href, title, text) {
  // Helper for object → string conversion
  const toStr = (v) =>
    typeof v === "string"
      ? v
      : v && typeof v === "object"
        ? v.href || v.url || "" // Supports marked v5 and later token formats
        : "";

  const hrefStr = toStr(href); // Actual URL
  const textStr =
    typeof text === "string" && text.trim() // Explicit link text
      ? text
      : hrefStr; // If not, show URL

  // If you can't get the URL, don't link to it
  if (!hrefStr) return textStr;

  const escapedHref = hrefStr.replace(/"/g, "&quot;");
  const escapedTitle = title ? String(title).replace(/"/g, "&quot;") : "";

  return `<a href="${escapedHref}"${
    escapedTitle ? ` title="${escapedTitle}"` : ""
  } target="_blank" rel="noopener noreferrer">${textStr}</a>`;
};

// Custom renderer to hold indentation
renderer.code = ({ text, lang }) => {
  const escape = (s) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<pre style="white-space: pre-wrap; margin: 0.5em 0; padding: 0.5em; background-color: #f5f5f5; border-radius: 4px; overflow-x: auto;">
<code class="language-${lang || ""}">${escape(text)}</code></pre>`;
};

// Simple implementation of markdown perspective
export function parseMarkdownWithAutoLink(text) {
  if (!text) return "";

  try {
    // marked settings
    marked.setOptions({
      gfm: true,
      breaks: true,
      sanitize: false,
      renderer: renderer,
    });

    // Convert Markdown to HTML
    let html = marked.parse(text);

    // Link text correction
    const linkMatches = text.match(/\[([^\]]+)\]\(([^)]+)\)/g);
    if (linkMatches) {
      linkMatches.forEach((match) => {
        const linkParts = match.match(/\[([^\]]+)\]\(([^)]+)\)/);
        if (linkParts && linkParts[1] && linkParts[2]) {
          const title = linkParts[1];
          const url = linkParts[2];

          // Fix a tag where URL is link text
          const escapedUrl = url.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(
            `<a[^>]*href="${escapedUrl}"[^>]*>${escapedUrl}</a>`,
            "g",
          );
          html = html.replace(
            regex,
            `<a href="${url}" target="_blank" rel="noopener noreferrer">${title}</a>`,
          );
        }
      });
    }

    // Convert unprocessed URLs to links
    html = html.replace(
      /(?!<a[^>]*>)(https?:\/\/[^\s<>"']+)(?![^<]*<\/a>)/g,
      '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
    );

    return html;
  } catch (e) {
    console.error("Error parsing markdown:", e);
    return `<p>${text}</p>`;
  }
}

const ChatbotAgentUI = () => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]); // Conversation history
  const [isComposing, setIsComposing] = useState(false); // IME Converting Flag
  const [feedbacks, setFeedbacks] = useState({}); // { [index]: "good"|"bad" }
  const lastEventIdRef = useRef(null); // Keep the latest event ID
  const scrollRef = useRef(null);
  const [userId, setUserId] = useState(null);
  const [isSessionCreate, setIsSessionCreate] = useState(false);
  // user_id Acquisition
  // const userId ="test_browser01"

  // session_id is newly generated for each page load
  const now = new Date();
  const pad = (n) => n.toString().padStart(2, "0");
  const yymmddhhmmss = [
    now.getFullYear().toString().slice(2), // yy
    pad(now.getMonth() + 1), // mm
    pad(now.getDate()), // dd
    pad(now.getHours()), // hh
    pad(now.getMinutes()), // mm
    pad(now.getSeconds()), // ss
  ].join("");

  const sessionIdRef = useRef("chat_" + yymmddhhmmss);

  // Fetch user from access token
  const fetchUser = React.useCallback(() => {
    const url = `${API_BASE_URL}/user`;
    return apiFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setUserId(data.user);
      })
      .catch((err) => {
        console.log(`fetch user error: ${err}`);
        setUserId("test_user01");
      });
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Request to create a session on page load
  useEffect(() => {
    if (userId !== null) {
      const appName = "car_dealer_agent";
      const sessionId = sessionIdRef.current;
      const url = `${API_BASE_URL}/apps/${appName}/users/${userId}/sessions/${sessionId}`;
      const body = {
        company: "03901",
      };

      apiFetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      })
        .then((res) => {
          if (res.ok) {
            console.log("Session creation request successful:", {
              url,
              body,
            });
            setIsSessionCreate(true);
          }
        })
        .catch((err) => {
          console.error("Failed to create a session:", err);
        });
    }
  }, [userId]);

  // Keep your scrolls up to date
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, loading]);

  const handleSend = async () => {
    if (!query.trim()) return;

    // --- Start time measurement ---
    const startTime = Date.now();
    // --- Start time measurement ---

    setHistory((prev) => [...prev, { role: "user", content: query }]);
    setLoading(true);
    setQuery("");

    let aiResponse = "";
    let citationLinks = [];

    try {
      const res = await apiFetch(`${API_BASE_URL}/run_sse`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          app_name: "car_dealer_agent",
          user_id: String(userId),
          session_id: sessionIdRef.current,
          new_message: {
            role: "user",
            parts: [
              {
                text: query,
              },
            ],
          },
          streaming: true,
        }),
      });

      if (!res.ok) {
        const result = await res.json().catch(() => ({}));
        console.error(result.exception_msg);
        setHistory((prev) => [
          ...prev,
          {
            role: "ai",
            content: result.msg,
            isBlock: true,
          },
        ]);
        return;
      }

      const reader = res.body?.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      if (reader) {
        let done = false;
        while (!done) {
          const { value, done: readerDone } = await reader.read();
          if (value) {
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            let lastNewline = buffer.lastIndexOf("\n");
            if (lastNewline !== -1) {
              const lines = buffer.slice(0, lastNewline).split("\n");
              buffer = buffer.slice(lastNewline + 1);
              let aiResponseChanged = false;
              for (const line of lines) {
                try {
                  const cleanLine = line.startsWith("data: ")
                    ? line.slice(6)
                    : line;
                  const json = JSON.parse(cleanLine);
                  if (json.partial === true) {
                    const aiText = json.content?.parts?.[0]?.text;
                    if (aiText) {
                      aiResponse += aiText;
                      aiResponseChanged = true;
                    }
                  } else {
                    lastEventIdRef.current = json.id;
                    const parts = json?.content?.parts || [];
                    for (const part of parts) {
                      const funcResp = part.functionResponse;
                      if (funcResp?.name === "web_search") {
                        const resultText = funcResp.response?.result || "";
                        const linkRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
                        let match;
                        const links = [];
                        while ((match = linkRegex.exec(resultText)) !== null) {
                          links.push({ title: match[1], url: match[2] });
                        }
                        links.forEach((link) => {
                          if (!citationLinks.some((l) => l.url === link.url)) {
                            citationLinks.push(link);
                          }
                        });
                      }
                    }
                  }
                } catch (e) {}
              }
              // Call setHistory only once after the loop ends
              if (aiResponseChanged) {
                // eslint-disable-next-line no-loop-func
                setHistory((prev) => {
                  const lastIdx = prev.length - 1;
                  if (lastIdx >= 0 && prev[lastIdx].role === "ai") {
                    return [
                      ...prev.slice(0, lastIdx),
                      { role: "ai", content: aiResponse },
                    ];
                  } else {
                    return [...prev, { role: "ai", content: aiResponse }];
                  }
                });
              }
            }
          }
          done = readerDone;
        }
        if (buffer.trim()) {
          try {
            const cleanBuffer = buffer.startsWith("data: ")
              ? buffer.slice(6)
              : buffer;
            const json = JSON.parse(cleanBuffer);
            if (json.partial === true) {
              const aiText = json.content?.parts?.[0]?.text;
              if (aiText) {
                aiResponse += aiText;
                setHistory((prev) => {
                  const lastIdx = prev.length - 1;
                  if (lastIdx >= 0 && prev[lastIdx].role === "ai") {
                    return [
                      ...prev.slice(0, lastIdx),
                      { role: "ai", content: aiResponse },
                    ];
                  } else {
                    return [...prev, { role: "ai", content: aiResponse }];
                  }
                });
              }
            }
          } catch (e) {}
        }
        // After streaming ends, web information is added to the end of the AI ​​response
        if (aiResponse && citationLinks.length > 0) {
          const priorityWords = [
            "toyota",
            "nissan",
            "mazda",
            "honda",
            "mitsubishi",
            "wikipedia",
            "youtube",
          ];
          const priorityLinks = citationLinks.filter((link) =>
            priorityWords.some((word) =>
              link.title.toLowerCase().includes(word),
            ),
          );
          const otherLinks = citationLinks.filter(
            (link) =>
              !priorityWords.some((word) =>
                link.title.toLowerCase().includes(word),
              ),
          );
          const selectedLinks = [...priorityLinks, ...otherLinks].slice(0, 3);

          let linksMarkdown = "";
          if (selectedLinks.length > 0) {
            linksMarkdown =
              "\n\n" +
              selectedLinks
                .map((link) => `[《WEB》${link.title}](${link.url})`)
                .join("　");
          }

          setHistory((prev) => {
            // Add web information to the end of AI answer history
            const lastIdx = prev.length - 1;
            if (lastIdx >= 0 && prev[lastIdx].role === "ai") {
              return [
                ...prev.slice(0, lastIdx),
                { role: "ai", content: prev[lastIdx].content + linksMarkdown },
              ];
            }
            return prev;
          });
        }
        // --- 時間計測終了・出力 ---
        const elapsed = Date.now() - startTime;
        console.log(`チャットボット応答完了: ${elapsed} ms`);
        // --- 時間計測終了・出力 ---
        if (lastEventIdRef.current && elapsed) {
          apiFetch(`${API_BASE_URL}/response_info`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              event_id: lastEventIdRef.current,
              latency_ms: elapsed,
              access_source: "chat",
            }),
          })
            .then(() => {
              console.log(
                `Response time sent: event_id=${lastEventIdRef.current}, time=${elapsed}`,
              );
            })
            .catch((err) => {
              console.error("Response time送信失敗:", err);
            });
        }
      }
    } catch (err) {
      console.error("Error fetching response:", err);
      setHistory((prev) => [
        ...prev,
        {
          role: "ai",
          content:
            "ขออภัยในความไม่สะดวกค่ะ\n กรุณาลองสอบถามอีกครั้งในภายหลัง ต้องขออภัยมา ณ ที่นี้ด้วยค่ะ \n",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Feedback Submission Handler
  const handleFeedback = (idx, type) => {
    setFeedbacks((prev) => ({ ...prev, [idx]: type }));
    const feedbackValue = type === "good" ? 0 : 1;
    const eventIdToSend = lastEventIdRef.current;
    if (eventIdToSend) {
      apiFetch(`${API_BASE_URL}/feedbacks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          event_id: eventIdToSend,
          feedback: feedbackValue,
        }),
      })
        .then(() => {
          console.log(
            `Feedback sent: event_id=${eventIdToSend}, feedback=${feedbackValue}`,
          );
        })
        .catch((err) => {
          console.error("Feedback submission failed:", err);
        });
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        flexDirection: "column",
        height: "95vh",
        flex: "1 1 0%",
      }}
    >
      {/* Conversation history area (top) */}
      <Box
        ref={scrollRef}
        sx={{
          background: "#ffffff",
          p: 2,
          borderRadius: 1,
          minHeight: 120,
          flex: 1,
          mb: 2,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {history.length === 0 && !loading && (
          <Typography variant="body1" color="text.secondary">
            {/* Your answer will appear here. */}
            คำตอบของคุณจะแสดงผลตรงนี้
          </Typography>
        )}
        {history.map((msg, idx) => (
          <React.Fragment key={idx}>
            {/* AI icon and label above speech bubble */}
            {msg.role === "ai" && (
              <Box
                sx={{ display: "flex", alignItems: "center", mb: 0.5, ml: 0.5 }}
              >
                <SmartToyIcon
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    marginRight: 1,
                    background: "#fff",
                    border: "1px solid #eee",
                    color: "#757575", // gray
                  }}
                />
              </Box>
            )}
            <Box
              sx={{
                alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                background: msg.role === "ai" ? "#f9f9f9" : "#4d4d4f", // Users use gray with a black color
                px: msg.role === "ai" ? 4 : 1.5,
                py: msg.role === "ai" ? 1.5 : 1,
                borderRadius: 1,
                maxWidth: "80%",
                wordBreak: "break-word",
                position: "relative",
              }}
            >
              {msg.role === "user" ? (
                <Typography
                  variant="body2"
                  color="white" // Users use white letters
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {msg.content}
                </Typography>
              ) : (
                <Box
                  sx={{
                    color: "black",
                    fontSize: "1rem",
                    "& pre": {
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                    },
                    "& ul, & ol": {
                      paddingLeft: "1.5em",
                    },
                    "& strong": {
                      fontWeight: "bold",
                    },
                    "& table": {
                      width: "100%",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #ccc",
                    },
                    "& th, & td": {
                      padding: "8px",
                      textAlign: "left",
                      borderRight: "1px solid #ccc",
                      borderBottom: "1px solid #ccc",
                    },
                    "& th": {
                      backgroundColor: "#EDEDED",
                    },
                    "& tr td:last-child, & tr th:last-child": {
                      borderRight: "none",
                    },
                    "& tr:last-child td": {
                      borderBottom: "none",
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: parseMarkdownWithAutoLink(msg.content ?? ""),
                  }}
                />
              )}
            </Box>
            {/* Show feedback button below AI answers */}
            {msg.role === "ai" && msg?.isBlock !== true && (
              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  display: "flex",
                  gap: 1,
                  alignSelf: "flex-start",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() => handleFeedback(idx, "good")}
                  sx={{
                    color: feedbacks[idx] === "good" ? "#07b815" : "#bdbdbd",
                    bgcolor:
                      feedbacks[idx] === "good" ? "#e8f5e9" : "transparent",
                    "&:hover": {
                      bgcolor:
                        feedbacks[idx] === "good" ? "#c8e6c9" : "#f5f5f5",
                    },
                  }}
                >
                  <ThumbUpIcon />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleFeedback(idx, "bad")}
                  sx={{
                    color: feedbacks[idx] === "bad" ? "#006400" : "#bdbdbd",
                    bgcolor:
                      feedbacks[idx] === "bad" ? "#e8f5e9" : "transparent",
                    "&:hover": {
                      bgcolor: feedbacks[idx] === "bad" ? "#c8e6c9" : "#f5f5f5",
                    },
                  }}
                >
                  <ThumbDownIcon />
                </IconButton>
              </Box>
            )}
          </React.Fragment>
        ))}
        {loading && (
          <Typography variant="body2" color="text.secondary">
            {/* Thinking ... */}
            กำลังประมวลผล...
          </Typography>
        )}
      </Box>
      {/* Input area (bottom) */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <TextField
          // Enter your question
          label="กรุณาพิมพ์คำถามของคุณ"
          variant="outlined"
          fullWidth
          multiline
          minRows={1}
          maxRows={6}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          onKeyDown={(e) => {
            // Shift+Enter for new line, Enter to send
            if (e.key === "Enter" && !e.shiftKey && query.trim() && !loading && !isComposing) {
              e.preventDefault();
              handleSend();
            }
            // Otherwise, allow Shift+Enter for new line (default behavior)
          }}
          sx={{
            "& .MuiInputBase-input": {
              fontSize: "0.95rem",
            },
          }}
        />
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={!isSessionCreate || loading}
        >
          {/* send */}
          ส่ง
        </Button>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, ml: 0.5, fontSize: "0.75rem", display: "block" }}
      >
        {/* [Note] These answers were generated by AI and accuracy cannot be guaranteed. Please be sure to check the authenticity of the information yourself. */}
        [หมายเหตุ] คำตอบเหล่านี้สร้างขึ้นโดย AI
        และไม่สามารถรับประกันความถูกต้องได้อย่างสมบูรณ์
        กรุณาตรวจสอบความถูกต้องของข้อมูลด้วยตนเอง
      </Typography>
    </Box>
  );
};

export default ChatbotAgentUI;
